import type { AnalyticsAdapter, AnalyticsEvent } from '../types';
import { getAnalyticsConfig } from '../config';

async function sha256(input: string): Promise<string> {
  const encoded = new TextEncoder().encode(input.trim().toLowerCase());
  const hash = await crypto.subtle.digest('SHA-256', encoded);
  return Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Google Ads conversion adapter.
 *
 * Piggybacks on the gtag.js script already loaded by GA4Adapter.
 * Only fires conversion pixels for events that have a configured label
 * in window.__ANALYTICS_CONFIG__.gadsConversions.
 */
export class GoogleAdsAdapter implements AnalyticsAdapter {
  name = 'google-ads';
  requiredConsent = 'marketing' as const;
  private awId: string;
  private conversions: Record<string, string>;
  private loaded = false;

  constructor(awId: string, conversions: Record<string, string>) {
    this.awId = awId;
    this.conversions = conversions;
  }

  load() {
    if (this.loaded || typeof window === 'undefined' || !window.gtag) return;
    this.loaded = true;
    window.gtag('config', this.awId, { send_page_view: false });
  }

  track(event: AnalyticsEvent) {
    if (typeof window === 'undefined' || !window.gtag) return;

    const label = this.conversions[event.name];
    if (!label) return;

    const params: Record<string, unknown> = {
      send_to: `${this.awId}/${label}`,
    };

    const amount = event.properties?.amount;
    if (typeof amount === 'number' && amount > 0) {
      params.value = amount;
      params.currency = (event.properties?.currency as string) ?? 'USD';
    }

    window.gtag('event', 'conversion', params);
  }

  async identify(_userId: string, traits?: Record<string, unknown>) {
    if (typeof window === 'undefined' || !window.gtag) return;
    const email = traits?.email;
    if (typeof email !== 'string' || !email) return;

    const hashed = await sha256(email);
    window.gtag('set', 'user_data', { sha256_email_address: hashed });
  }

  reset() {
    if (typeof window === 'undefined' || !window.gtag) return;
    window.gtag('set', 'user_data', {});
  }
}

/**
 * Factory that reads config from the runtime-injected window.__ANALYTICS_CONFIG__.
 * Returns null if Google Ads is not configured.
 */
export function createGoogleAdsAdapter(): GoogleAdsAdapter | null {
  const config = getAnalyticsConfig();
  if (!config.googleAdsId) return null;

  const conversions = Object.fromEntries(
    Object.entries(config.gadsConversions).filter(([, v]) => v),
  );
  return new GoogleAdsAdapter(config.googleAdsId, conversions);
}
